import type { AdminLicense, LicenseStatus, TranslationProvider } from '@sky-lingo-v2/shared';

export type AccessUpdate = { providers?: TranslationProvider[]; expiresAt?: string; status?: LicenseStatus };
export type AccessRequest = <T>(path: string, options?: RequestInit) => Promise<T>;
export interface AccessDraft { providers: TranslationProvider[]; expiresAt: string; status: LicenseStatus | '' }

export function userAccessPath(userId: number): string {
  if (!Number.isSafeInteger(userId) || userId < 1) throw new Error('invalid_access_owner');
  return `/api/admin/users/${userId}/access`;
}

export async function readUserAccess(request: AccessRequest, userId: number): Promise<AdminLicense | null> {
  const result = await request<{ access: AdminLicense | null }>(userAccessPath(userId));
  if (!Object.hasOwn(result, 'access')) throw new Error('access_response_invalid');
  if (result.access && result.access.userId !== userId) throw new Error('access_owner_mismatch');
  return result.access;
}

export async function saveUserAccess(request: AccessRequest, userId: number, value: AccessUpdate): Promise<AdminLicense> {
  const result = await request<{ access: AdminLicense }>(userAccessPath(userId), { method: 'PUT', body: JSON.stringify(value) });
  if (!result.access || result.access.userId !== userId) throw new Error('access_owner_mismatch');
  return result.access;
}

export function accessLocalDateTime(value: string | Date): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  const pad = (number: number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function expiry(value: string): string {
  const date = new Date(value);
  if (!value || !Number.isFinite(date.getTime())) throw new Error('invalid_expiry');
  return date.toISOString();
}

export function accessDraftPayload(draft: AccessDraft, original?: AdminLicense | null, unknownCurrent = false): AccessUpdate {
  if (unknownCurrent) {
    const partial: AccessUpdate = {};
    if (draft.providers.length) partial.providers = [...draft.providers];
    if (draft.expiresAt) partial.expiresAt = expiry(draft.expiresAt);
    if (draft.status) {
      if (!['active', 'expired', 'revoked'].includes(draft.status)) throw new Error('current_access_missing_fields');
      partial.status = draft.status as LicenseStatus;
    }
    return partial;
  }
  if (!draft.providers.length) throw new Error('invalid_provider_policy');
  if (!['active', 'expired', 'revoked'].includes(draft.status)) throw new Error('current_access_missing_fields');
  if (!original) return { providers: [...draft.providers], expiresAt: expiry(draft.expiresAt), status: draft.status as LicenseStatus };
  const update: AccessUpdate = {};
  if (JSON.stringify(draft.providers) !== JSON.stringify(original.providers)) update.providers = [...draft.providers];
  // Untouched fields retain the original instant, including stored sub-second precision.
  if (draft.expiresAt !== accessLocalDateTime(original.expiresAt)) update.expiresAt = expiry(draft.expiresAt);
  if (draft.status !== original.status) update.status = draft.status as LicenseStatus;
  return update;
}

export function canOpenUserManagement(can: (permission: 'users.read' | 'licenses.read' | 'licenses.write') => boolean): boolean {
  return can('users.read') || can('licenses.read') || can('licenses.write');
}
